import React, { useState } from "react";
import {
    ColumnDef,
    getCoreRowModel,
    useReactTable,
    flexRender,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";
import { BusinessUnit } from "@/types/system-setting/business-unit";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import useTableData from "@/components/ui/table/data-table-logic";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { DataTableRowActions } from "@/components/admin/business_unit/table/data-table-row-actions";
import BusinessUnitForm from "../modal/form";
import { useGetCategoriesQuery } from "@/services/categoryApi";

const initialValue = {
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "id",
};

export default function BusinessUnitTableView() {
    const business_unit = (() => {
        try {
            return (
                JSON.parse(localStorage.getItem("accessLevels") as string).business_unit ??
                "no_access"
            );
        } catch {
            return "no_access";
        }
    })();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState<BusinessUnit | undefined>(undefined);
    const [action, setAction] = useState<"add" | "edit">("add");

    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

    const {
        searchCriteria,
        query,
        toggleSortOrder,
        setSearchCriteria,
        inputValue,
        setInputValue,
    } = useTableData(initialValue);

    const { data: businessUnitsData, isLoading, refetch } = useGetBusinessUnitsQuery({
        org_id: orgId,
        ...searchCriteria,
    });

    const { data: facilityCategoryData } = useGetCategoriesQuery({ org_id: orgId, query: "type=facility" });
    const { data: membershipGroupData } = useGetCategoriesQuery({ org_id: orgId, query: "type=membership" });
    const { data: productCategoryData } = useGetCategoriesQuery({ org_id: orgId, query: "type=product" });
    const { data: creditAddOnsData } = useGetCategoriesQuery({ org_id: orgId, query: "type=credit_add_ons" });

    const categoryMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        facilityCategoryData?.data?.forEach((cat) => {
            if (cat.id) map[cat.id] = cat.name;
        });
        return map;
    }, [facilityCategoryData]);

    const groupMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        membershipGroupData?.data?.forEach((cat) => {
            if (cat.id) map[cat.id] = cat.name;
        });
        return map;
    }, [membershipGroupData]);

    const productCategoryMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        productCategoryData?.data?.forEach((cat) => {
            if (cat.id) map[cat.id] = cat.name;
        });
        return map;
    }, [productCategoryData]);

    const creditAddOnMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        creditAddOnsData?.data?.forEach((cat) => {
            if (cat.id) map[cat.id] = cat.name;
        });
        return map;
    }, [creditAddOnsData]);

    const renderBadges = (ids: number[] | null | undefined, map: Record<number, string>) => {
        if (!ids || ids.length === 0) return <span className="text-gray-400">-</span>;

        const names = ids.map((id) => map[id]).filter(Boolean);
        if (names.length === 0) return <span className="text-gray-400">-</span>;

        const visibleBadges = names.slice(0, 2);
        const remainingNames = names.slice(2);
        const remaining = remainingNames.length;

        return (
            <div className="flex flex-wrap gap-1 items-center">
                {visibleBadges.map((name, index) => (
                    <span
                        key={index}
                        className="inline-block rounded-full bg-green-100 text-green-800 px-3 py-1 text-xs font-medium"
                    >
                        {name}
                    </span>
                ))}
                {remaining > 0 && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-block rounded-full bg-gray-100 text-gray-600 px-3 py-1 text-xs font-medium cursor-pointer">
                                    +{remaining}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                <ul className="flex flex-col gap-1 p-1">
                                    {remainingNames.map((name, idx) => (
                                        <li key={idx} className="whitespace-nowrap">{name}</li>
                                    ))}
                                </ul>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
        );
    };

    const handleEdit = React.useCallback((data: BusinessUnit) => {
        setFormData(data);
        setAction("edit");
        setIsDialogOpen(true);
    }, []);

    const actionsColumn: ColumnDef<BusinessUnit> = {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
            <DataTableRowActions
                access={business_unit}
                data={row.original}
                refetch={refetch}
                handleEdit={handleEdit}
            />
        ),
    };

    const columns: ColumnDef<BusinessUnit>[] = React.useMemo(() => [
        {
            accessorKey: "name",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Business Unit Name"
                    sortKey="name"
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <span className="capitalize font-medium">{row.original.name}</span>
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "facility_category_ids",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Facility Categories"
                    sortKey="facility_category_ids"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) =>
                renderBadges(
                    row.original.facility_category_ids?.map(Number) ?? [],
                    categoryMap
                ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "membership_group_ids",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Membership Groups"
                    sortKey="membership_group_ids"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) =>
                renderBadges(
                    row.original.membership_group_ids?.map(Number) ?? [],
                    groupMap
                ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "product_category_ids",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Product Categories"
                    sortKey="product_category_ids"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) =>
                renderBadges(
                    row.original.product_category_ids?.map(Number) ?? [],
                    productCategoryMap
                ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "credit_add_ons_ids",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Credits"
                    sortKey="credit_add_ons_ids"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) =>
                renderBadges(
                    row.original.credit_add_ons_ids?.map(Number) ?? [],
                    creditAddOnMap
                ),
            enableSorting: false,
            enableHiding: false,
        },
        ...(business_unit !== "read" ? [actionsColumn] : []),
    ], [refetch, handleEdit, categoryMap, groupMap, productCategoryMap, creditAddOnMap, searchCriteria, toggleSortOrder, business_unit]);

    const apiTotal = businessUnitsData?.filtered_counts || 0;
    const table = useReactTable({
        data: businessUnitsData?.data || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const {
        handleLimitChange,
        handleNextPage,
        handlePrevPage,
        handleFirstPage,
        handleLastPage,
        isLastPage,
    } = usePagination({
        totalRecords: apiTotal,
        searchCriteria,
        setSearchCriteria,
    });

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
                <TableSearchFilter
                    setSearchCriteria={setSearchCriteria}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                />
                {business_unit !== "read" && (
                    <Button
                        className="bg-primary text-black flex items-center gap-1 h-8 px-2"
                        onClick={() => {
                            setFormData(undefined);
                            setAction("add");
                            setIsDialogOpen(true);
                        }}
                    >
                        <PlusIcon className="h-4 w-4" />
                        Create New
                    </Button>
                )}
            </div>

            <div className="rounded-none border border-border">
                <ScrollArea className="w-full relative">
                    <ScrollBar orientation="horizontal" className="relative z-30 cursor-grab h-2" />
                    <Table className="w-full">
                        <TableHeader className="bg-secondary">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        <div className="flex space-x-2 justify-center items-center bg-white">
                                            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                            <div className="size-3 bg-black rounded-full animate-bounce"></div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                        onClick={(e) => {
                                            if (business_unit !== "read" && (e.target instanceof Element &&
                                                !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                                                handleEdit(row.original);
                                            }
                                        }}
                                        className={business_unit !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        No records found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </ScrollArea>
            </div>

            {(businessUnitsData?.data?.length ?? 0) > 0 && (
                <Pagination
                    limit={searchCriteria.limit}
                    offset={searchCriteria.offset}
                    totalItems={businessUnitsData?.total_counts || 0}
                    onLimitChange={handleLimitChange}
                    onNextPage={handleNextPage}
                    onPrevPage={handlePrevPage}
                    onFirstPage={handleFirstPage}
                    onLastPage={handleLastPage}
                    isLastPage={isLastPage}
                />
            )}

            <BusinessUnitForm
                isOpen={isDialogOpen}
                setOpen={setIsDialogOpen}
                data={formData}
                setData={setFormData}
                action={action}
                setAction={setAction}
                refetch={refetch}
            />
        </div>
    );
}